
<!-- src/views/ProjectDetails.vue -->
<template>
  <v-form class="project-details-page" @submit.prevent>
    <div class="d-flex justify-end mb-2">
      <ProjectPageNav />
    </div>
    <v-row>
      <!-- Left column -->
      <v-col cols="12" md="6">
        <v-card class="details-card" elevation="2">
          <v-card-title class="details-card-header">
            <div class="details-header-icon">
              <v-icon color="primary">mdi-domain</v-icon>
            </div>

            <div>
              <div class="text-h6 font-weight-bold">
                Allgemeine Daten
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Grundinformationen und Standortdaten des Projekts.
              </div>
            </div>
          </v-card-title>

          <v-card-text class="details-card-body">
            <v-text-field
              v-model="name"
              :error-messages="fieldErrors.name"
              label="Projektbezeichnung"
              :hint="hint?.name"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-clipboard-text-outline"
            />

            <v-textarea
              v-model="note"
              :error-messages="fieldErrors.note"
              label="Bemerkung"
              :hint="hint?.note"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-note-edit-outline"
              rows="2"
              auto-grow
            />

            <v-text-field
              v-model="street"
              :error-messages="fieldErrors.street"
              label="Straße/Hausnummer"
              :hint="hint?.street"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-home-map-marker"
            />

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="postcode"
                  :error-messages="fieldErrors.postcode"
                  label="PLZ"
                  :hint="hint?.postcode"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-city"
                  type="text"
                  inputmode="numeric"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="city"
                  :error-messages="fieldErrors.city"
                  label="Stadt"
                  :hint="hint?.city"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-office-building"
                />
              </v-col>
            </v-row>

            <!-- Branche -->
            <v-autocomplete
              v-model="branch"
              :error-messages="fieldErrors.branch"
              label="Branche"
              :items="branches"
              item-title="name"
              item-value="id"
              variant="outlined"
              density="comfortable"
              clearable
              chips
            >
              <template #append-inner>
                <v-tooltip v-if="hint?.branch" color="surface" content-class="tooltip">
                  <template #activator="{ props }">
                    <v-icon v-bind="props">mdi-information</v-icon>
                  </template>
                  <span>{{ hint.branch }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>

            <!-- Kategorie -->
            <v-autocomplete
              v-model="category"
              :error-messages="fieldErrors.category"
              label="Kategorie"
              :items="categories"
              item-title="name"
              item-value="id"
              :disabled="!branch"
              variant="outlined"
              density="comfortable"
              clearable
              chips
            >
              <template #append-inner>
                <v-tooltip v-if="hint?.category" color="surface" content-class="tooltip">
                  <template #activator="{ props }">
                    <v-icon v-bind="props">mdi-information</v-icon>
                  </template>
                  <span>{{ hint.category }}</span>
                </v-tooltip>
              </template>
            </v-autocomplete>

            <v-text-field
              v-model="area"
              :error-messages="fieldErrors.area"
              label="Bruttogrundfläche"
              suffix="m²"
              :hint="hint?.area"
              variant="outlined"
              density="comfortable"
              type="number"
              inputmode="decimal"
              min="0"
              step="0.01"
              prepend-inner-icon="mdi-ruler-square"
            />

            <div class="d-flex align-center mt-2">
              <v-btn
                color="primary"
                variant="tonal"
                @click="runLocalise"
                :loading="geocoding"
                class="me-2"
                prepend-icon="mdi-map-marker-radius"
              >
                Standort ermitteln
              </v-btn>

              <!-- Nur ein- und ausblenden: löst bewusst keine neue Anfrage aus. -->
              <v-btn
                v-if="hasCoordinates"
                variant="text"
                @click="toggleMap"
                :prepend-icon="localise ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              >
                {{ localise ? 'Karte ausblenden' : 'Karte anzeigen' }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <MapContainer
          v-if="localise && hasCoordinates"
          :mapLon="lon"
          :mapLat="lat"
          :resolved-address="locationResolvedAddress"
          draggable
          class="mt-4"
          @update:position="onMarkerMoved"
        />

        <!--
          Vorschlag statt Übernahme: die nächstgelegene OSM-Adresse ist bei
          großen Arealen oft die falsche, die eingetippte meist die richtige.
        -->
        <v-alert
          v-if="addressSuggestion"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          <div class="text-body-2">
            OSM schlägt für diesen Punkt vor:
            <strong>{{ addressSuggestion.display_name }}</strong>
          </div>

          <template #append>
            <v-btn size="small" variant="text" @click="applyAddressSuggestion">
              Übernehmen
            </v-btn>

            <v-btn size="small" variant="text" @click="addressSuggestion = null">
              Verwerfen
            </v-btn>
          </template>
        </v-alert>

        <div v-if="locationSource === 'manual' && hasCoordinates" class="d-flex align-center mt-2">
          <v-chip size="small" color="primary" variant="tonal" prepend-icon="mdi-cursor-move">
            Standort manuell gesetzt
          </v-chip>

          <v-btn size="small" variant="text" class="ms-2" @click="resetLocationToAddress">
            Zurück zur Adresse
          </v-btn>
        </div>
        
        <v-card rounded="lg" elevation="2" class="mt-4 overflow-hidden">
          <v-card-text class="pa-0">
            <StatsCard :projectData="dataComputed" :projectValid="valid" :project-name="project?.name" />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right column -->
      <v-col cols="12" md="6">
        <v-card class="details-card" elevation="2">
          <v-card-title class="details-card-header">
            <div class="details-header-icon">
              <v-icon color="primary">mdi-lightning-bolt-outline</v-icon>
            </div>

            <div>
              <div class="text-h6 font-weight-bold">
                Energiedaten
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Verbrauch, Kosten, Faktoren und Emissionswerte erfassen.
              </div>
            </div>

            <v-spacer />

            <v-tooltip text="Hilfe anzeigen" location="bottom" :disabled="store.sidebarShown" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  @click="store.sidebarShown ? store.hideSidebar() : store.showSidebar(ProjectDetailsHelp)"
                  color="primary"
                  variant="flat"
                  class="details-help-button"
                >
                  <v-icon start>
                    {{ store.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
                  </v-icon>
                  Hilfe
                </v-btn>
              </template>
            </v-tooltip>
          </v-card-title>

          <v-row class="justify-end">
            <div class="w-100">
              <v-container>
                <HelpBar :help="ProjectDetailsHelp" />
              </v-container>
            </div>
          </v-row>

          <v-expansion-panels accordion rounded class="px-4 pb-4">
            <!-- Energieverbrauch und Kosten -->
            <v-expansion-panel>
              <v-expansion-panel-title expand-icon="mdi-chevron-down">
                <div class="heading">Energieverbrauch und Kosten aus Energieabrechnungen</div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-select
                  v-model="year"
                  :items="years"
                  label="Jahr"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                />
                <div class="energy-year-scroll">
                  <EnergyStatsYear
                    v-if="energyConsumptionData[yearIndex]"
                    v-model="energyConsumptionData[yearIndex]"
                    :fuels="fuelsSupply"
                    :errors="supplyConsumptionErrors"
                    :hintFuel="hintFuel.energyConsumptionData"
                    :feed="false"
                  />
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- Energiepreise -->
            <v-expansion-panel>
              <v-expansion-panel-title expand-icon="mdi-chevron-down">
                <div class="heading">Energiepreise für Berechnung</div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-table ref="priceTable" fixed-header height="300" class="energy-table mt-4">
                  <thead>
                    <tr>
                      <th class="text-left">Energieträger</th>
                      <th class="text-left">Energiepreis (€/kWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in fuelsSupply" :key="item.id">
                      <!-- Die selbst benannten Wärmequellen werden hier
                           definiert: Name und Lieferart stehen dort, wo man
                           ihnen auch den Preis gibt. Die übrigen Tabellen
                           übernehmen den Namen über `fuelsSupply`. -->
                      <td v-if="isCustomHeat(item.id)" class="custom-heat-cell">
                        <v-text-field
                          v-model="energyCustomName[item.id]"
                          :error-messages="energyCustomNameErrors[item.id]"
                          label="Bezeichnung"
                          placeholder="z. B. Biogas"
                          variant="outlined"
                          density="compact"
                          hide-details="auto"
                        />

                        <v-select
                          v-model="energyHeatDelivery[item.id]"
                          :items="HEAT_DELIVERY_OPTIONS"
                          label="Lieferart"
                          variant="outlined"
                          density="compact"
                          hide-details="auto"
                          class="mt-2"
                        />

                        <v-btn
                          @click="removeCustomHeatSource(item.id)"
                          variant="text"
                          size="small"
                          color="error"
                          prepend-icon="mdi-close"
                          class="mt-1"
                        >
                          Entfernen
                        </v-btn>
                      </td>

                      <td v-else>
                        {{ item.name }}
                        <!-- Bei den lokal erzeugten Erneuerbaren ist 0 eine
                             Angabe und keine Lücke — das steht sonst nirgends. -->
                        <div v-if="isLocallyProduced(item.id)" class="fuel-note">
                          lokal erzeugt — Preis 0 zulässig
                        </div>
                      </td>
                      <td>
                        <v-text-field
                          label=""
                          v-model="energyPriceData[item.id]"
                          placeholder="0"
                          :error-messages="energyPriceDataErrors[item.id]"
                          variant="outlined"
                          density="compact"
                          type="number"
                          inputmode="decimal"
                          min="0"
                          step="0.001"
                          suffix="€/kWh"
                          class="mt-2"
                        >
                          <template #append-inner>
                            <v-tooltip v-if="hintFuel.energyPriceData[item.name]" color="surface" content-class="tooltip">
                              <template #activator="{ props }">
                                <v-icon v-bind="props">mdi-information</v-icon>
                              </template>
                              <span>{{ hintFuel.energyPriceData[item.name] }}</span>
                            </v-tooltip>

                            <v-btn
                              @click="getStandardParameter(item.id, 'energyPriceData')"
                              title="Standardwert nehmen"
                              icon
                              size="x-small"
                              variant="plain"
                              density="compact"
                              color="black"
                              class="pb-5 ml-2 standard-value-btn"
                            >
                              <v-icon size="18">mdi-cog-counterclockwise</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </v-table>

                <!-- Wärme ist die einzige Nutzenergie mit beliebig vielen
                     Quellen; Strom, Kälte und die Netzbezüge stehen fest. -->
                <div class="d-flex align-center mt-3">
                  <v-btn
                    @click="addCustomHeatSource"
                    :disabled="!canAddCustomHeat"
                    variant="tonal"
                    color="primary"
                    size="small"
                    prepend-icon="mdi-plus"
                  >
                    Wärmequelle hinzufügen
                  </v-btn>

                  <span class="text-caption text-medium-emphasis ms-3">
                    {{
                      canAddCustomHeat
                        ? 'Für Träger ausserhalb des Katalogs — Biogas, Flüssiggas, zugekaufte Abwärme.'
                        : 'Alle drei Plätze für eigene Wärmequellen sind belegt.'
                    }}
                  </span>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- Primärenergiefaktoren -->
            <v-expansion-panel>
              <v-expansion-panel-title expand-icon="mdi-chevron-down">
                <div class="heading">Primärenergiefaktoren</div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Zwei Spalten, weil DIN V 18599-1 Tabelle A.1 zwei Faktoren
                     führt: der nicht erneuerbare ist der, den das GEG bewertet,
                     der Gesamtfaktor zeigt den tatsächlichen Ressourceneinsatz.
                     Bei Holz liegen sie um den Faktor sechs auseinander. -->
                <v-table fixed-header height="300" class="energy-table mt-4">
                  <thead>
                    <tr>
                      <th class="text-left">Energieträger</th>
                      <th class="text-left">f<sub>P</sub> nicht erneuerbar</th>
                      <th class="text-left">f<sub>P</sub> gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in fuelsSupply" :key="item.id">
                      <td>{{ item.name }}</td>
                      <td>
                        <v-text-field
                          label=""
                          v-model="energyPerformanceData[item.id]"
                          placeholder="0"
                          :error-messages="energyPerformanceDataErrors[item.id]"
                          variant="outlined"
                          density="compact"
                          type="number"
                          inputmode="decimal"
                          min="0"
                          step="0.001"
                          class="mt-2"
                        >
                          <template #append-inner>
                            <v-tooltip v-if="hintFuel.energyPerformanceData[item.name]" color="surface" content-class="tooltip">
                              <template #activator="{ props }">
                                <v-icon v-bind="props">mdi-information</v-icon>
                              </template>
                              <span>{{ hintFuel.energyPerformanceData[item.name] }}</span>
                            </v-tooltip>

                            <v-btn
                              @click="getStandardParameter(item.id, 'energyPerformanceData')"
                              title="Standardwert nehmen"
                              icon
                              size="x-small"
                              variant="plain"
                              density="compact"
                              color="black"
                              class="pb-5 ml-2 standard-value-btn"
                            >
                              <v-icon size="18">mdi-cog-counterclockwise</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </td>
                      <td>
                        <v-text-field
                          label=""
                          v-model="energyPerformanceTotalData[item.id]"
                          placeholder="Katalogwert"
                          :error-messages="energyPerformanceTotalDataErrors[item.id]"
                          variant="outlined"
                          density="compact"
                          type="number"
                          inputmode="decimal"
                          min="0"
                          step="0.001"
                          class="mt-2"
                          hint="Leer lassen, um den Katalogwert zu verwenden."
                        >
                          <template #append-inner>
                            <v-btn
                              @click="getStandardParameter(item.id, 'energyPerformanceTotalData')"
                              title="Standardwert nehmen"
                              icon
                              size="x-small"
                              variant="plain"
                              density="compact"
                              color="black"
                              class="pb-5 ml-2 standard-value-btn"
                            >
                              <v-icon size="18">mdi-cog-counterclockwise</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- CO₂-Äquivalente -->
            <v-expansion-panel>
              <v-expansion-panel-title expand-icon="mdi-chevron-down">
                <div class="heading">CO₂-Äquivalente</div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- GHG Protocol Scope 2 verlangt beide Sichtweisen
                     nebeneinander: standortbasiert zeigt eine Ökostrom-
                     Umstellung gar nicht, marktbasiert allein lässt einen
                     Standort emissionsfrei aussehen, der unverändert am selben
                     Netz hängt. -->
                <v-table fixed-header height="300" class="energy-table mt-4">
                  <thead>
                    <tr>
                      <th class="text-left">Energieträger</th>
                      <th class="text-left">standortbasiert (g/kWh)</th>
                      <th class="text-left">marktbasiert (g/kWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in fuelsSupply" :key="item.id">
                      <td>{{ item.name }}</td>
                      <td>
                        <v-text-field
                          label=""
                          v-model="energyCo2Data[item.id]"
                          placeholder="0"
                          :error-messages="energyCo2DataErrors[item.id]"
                          variant="outlined"
                          density="compact"
                          type="number"
                          inputmode="decimal"
                          min="0"
                          step="0.1"
                          suffix="g/kWh"
                          class="mt-2"
                        >
                          <template #append-inner>
                            <v-tooltip v-if="hintFuel.energyCo2Data[item.name]" color="surface" content-class="tooltip">
                              <template #activator="{ props }">
                                <v-icon v-bind="props">mdi-information</v-icon>
                              </template>
                              <span>{{ hintFuel.energyCo2Data[item.name] }}</span>
                            </v-tooltip>

                            <v-btn
                              @click="getStandardParameter(item.id, 'energyCo2Data')"
                              title="Standardwert nehmen"
                              icon
                              size="x-small"
                              variant="plain"
                              density="compact"
                              color="black"
                              class="pb-5 ml-2 standard-value-btn"
                            >
                              <v-icon size="18">mdi-cog-counterclockwise</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </td>
                      <td>
                        <v-text-field
                          label=""
                          v-model="energyCo2MarketData[item.id]"
                          placeholder="wie standortbasiert"
                          :error-messages="energyCo2MarketDataErrors[item.id]"
                          variant="outlined"
                          density="compact"
                          type="number"
                          inputmode="decimal"
                          min="0"
                          step="0.1"
                          suffix="g/kWh"
                          class="mt-2"
                          hint="Nur bei vertraglichem Nachweis — Ökostromprodukt oder Herkunftsnachweis."
                        />
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>

        <!-- Einspeisedaten -->
        <v-card class="details-card mt-4" elevation="2">
          <v-card-title class="details-card-header">
            <div class="details-header-icon">
              <v-icon color="primary">mdi-transmission-tower-export</v-icon>
            </div>

            <div>
              <div class="text-h6 font-weight-bold">
                Einspeisedaten
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Einspeiseenergie und Vergütungsdaten verwalten.
              </div>
            </div>
          </v-card-title>

          <!-- Gilt für das ganze Projekt, nicht für ein Jahr: deshalb über den Panels. -->
          <v-card-text class="pt-0 px-4 pb-0">
            <v-checkbox
              v-model="creditExportedEnergy"
              :error-messages="fieldErrors.credit_exported_energy"
              label="CO₂-Gutschrift für Einspeisung"
              :hint="hint?.credit_exported_energy"
              persistent-hint
              density="comfortable"
            />
          </v-card-text>

          <v-expansion-panels accordion rounded class="px-4 pb-4">
            <!-- Energieverbrauch und -vergütung -->
            <v-expansion-panel>
              <v-expansion-panel-title expand-icon="mdi-chevron-down">
                <div class="heading">Einspeiseenergie und -vergütung aus Energieabrechnungen</div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-select
                  v-model="year"
                  :items="years"
                  label="Jahr"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                />
                
                <div class="energy-year-scroll">
                  <EnergyStatsYear
                    v-if="energyConsumptionData[yearIndex]"
                    v-model="energyConsumptionData[yearIndex]"
                    :fuels="fuelsFD"
                    :errors="feedConsumptionErrors"
                    :hintFuel="hintFuel.energyConsumptionData"
                    :feed="true"
                  />
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <!-- Einspeisevergütung -->
            <v-expansion-panel>
              <v-expansion-panel-title expand-icon="mdi-chevron-down">
                <div class="heading">Einspeisevergütung</div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-table fixed-header height="300" class="energy-table mt-4">
                  <thead>
                    <tr>
                      <th class="text-left">Energieträger</th>
                      <th class="text-left">Energiepreis (€/kWh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in fuelsFD" :key="item.id">
                      <td>
                        {{ item.name }}
                        <!-- Nach Ablauf der EEG-Vergütung speist die Anlage
                             weiter ein, ohne etwas dafür zu bekommen. -->
                        <div v-if="isFeedIn(item.id)" class="fuel-note">
                          ohne Vergütung — 0 zulässig
                        </div>
                      </td>
                      <td>
                        <v-text-field
                          label=""
                          v-model="energyPriceData[item.id]"
                          placeholder="0"
                          :error-messages="energyPriceDataErrors[item.id]"
                          variant="outlined"
                          density="compact"
                          type="number"
                          inputmode="decimal"
                          min="0"
                          step="0.001"
                          suffix="€/kWh"
                          class="mt-2"
                        >
                          <template #append-inner>
                            <v-tooltip v-if="hintFuel.energyPriceData[item.name]" color="surface" content-class="tooltip">
                              <template #activator="{ props }">
                                <v-icon v-bind="props">mdi-information</v-icon>
                              </template>
                              <span>{{ hintFuel.energyPriceData[item.name] }}</span>
                            </v-tooltip>

                            <v-btn
                              @click="getStandardParameter(item.id, 'energyPriceData')"
                              title="Standardwert nehmen"
                              icon
                              size="x-small"
                              variant="plain"
                              density="compact"
                              color="black"
                              class="pb-5 ml-2 standard-value-btn"
                            >
                              <v-icon size="18">mdi-cog-counterclockwise</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="details-save-card mt-5" elevation="1">
      <div class="details-save-content">
        <div>
          <div class="text-subtitle-1 font-weight-bold">
            Projektdaten speichern
          </div>
          <div class="text-body-2 text-medium-emphasis">
            Speichern Sie alle allgemeinen und energetischen Projektdaten.
          </div>
        </div>

        <v-btn
          @click="submit"
          :disabled="!valid || saving"
          color="primary"
          variant="elevated"
          :loading="saving"
          class="save-button"
        >
          <v-icon start>mdi-content-save</v-icon>
          Speichern
        </v-btn>
      </div>
    </v-card>
  </v-form>
</template>


<script setup>
import { ref, computed, onBeforeMount, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/plugins/axios'
import { useProjectStore } from '@/stores/projectStore'
import HelpBar from '@/components/HelpBar.vue'
import ProjectPageNav from '@/components/ProjectPageNav.vue'
import ProjectDetailsHelp from '@/components/help/ProjectDetailsHelp.vue'
import StatsCard from '@/components/projectstats/StatsCard.vue'
import MapContainer from '@/components/MapContainer.vue'
import EnergyStatsYear from '@/components/project/EnergyStatsYear.vue'
import { useValidation } from '@/composables/useValidation'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'

const store = useProjectStore()
const route = useRoute()
const router = useRouter()

const ownerOverride = ref(null)
const name = ref(null)
const note = ref(null)
const city = ref(null)
const postcode = ref(null)
const street = ref(null)
const branch = ref(null)
const category = ref(null)
const area = ref(null)
const creditExportedEnergy = ref(false)

const saving = ref(false) // NEW: submit loading state

const hint = ref(null)
const hintFuel = ref({
  energyConsumptionData: {
    Netzstrom: '',
    'Erdgas L': '',
    'Erdgas H': '',
    Öl: 'Umrechnungsfaktor 10,6 kWh pro Liter HEL wird verwendet',
    Pellets: '',
    Hackschnitzel: '',
    Fernwärme: '',
  },
  energyPriceData: {
    Netzstrom: '',
    'Erdgas L': '',
    'Erdgas H': '',
    Öl: '',
    Pellets: '',
    Hackschnitzel: '',
    Fernwärme: '',
  },
  energyPerformanceData: {
    Netzstrom: '',
    'Erdgas L': '',
    'Erdgas H': '',
    Öl: '',
    Pellets: '',
    Hackschnitzel: '',
    Fernwärme: '',
  },
  energyCo2Data: {
    Netzstrom: '',
    'Erdgas L': '',
    'Erdgas H': '',
    Öl: '',
    Pellets: '',
    Hackschnitzel: '',
    Fernwärme: '',
  },
})

const fields = {
  owner: 'owner',
  name: 'name',
  note: 'note',
  postcode: 'postcode',
  street: 'street',
  city: 'city',
  lat: 'latitude',
  lon: 'longitude',
  locationSource: 'location_source',
  locationResolvedAddress: 'location_resolved_address',
  branch: 'branch',
  category: 'category',
  area: 'area',
  creditExportedEnergy: 'credit_exported_energy',
  energyDataPayload: 'energy_data',
  energyConsumptionDataPayload: 'energy_consumption_data',
}

const energyCo2Data = ref({})
const energyCo2MarketData = ref({})
const energyPerformanceData = ref({})
const energyPerformanceTotalData = ref({})
const energyPriceData = ref({})
const priceTable = ref(null)
const energyCustomName = ref({})
const energyHeatDelivery = ref({})
const addedCustomHeatIds = ref([])

const energyConsumptionData = ref({})
const energyConsumptionDataLastPayload = ref([])

const branches = ref([])
const categories = ref([])
const fuels = ref([])
const fuelsFD = ref([])

/* Ein abgekündigter Energieträger (derzeit „Erdgas L": die Marktraumumstellung ersetzt
   L-Gas bis 2030) verschwindet aus der Auswahl — außer wo bereits Verbrauch oder Kosten
   hinterlegt sind, sonst wären gespeicherte Daten weder sicht- noch korrigierbar. */
const usedFuelIds = computed(() => {
  const used = new Set()
  for (const perYear of Object.values(energyConsumptionData.value ?? {})) {
    for (const [fuelId, entry] of Object.entries(perYear ?? {})) {
      if (entry?.consumption != null || entry?.cost != null) used.add(Number(fuelId))
    }
  }
  return used
})

/* Träger, bei denen 0 eine Aussage ist und keine fehlende Angabe: die lokal
   erzeugten Erneuerbaren (21–23) werden nicht eingekauft, und eine Anlage nach
   Ablauf der EEG-Vergütung speist ein, ohne etwas dafür zu bekommen (31–33).
   Der Server erzwingt dieselbe Abgrenzung — siehe `ZERO_COST_FUEL_IDS` in
   projects/models.py. */
const LOCALLY_PRODUCED_FUEL_IDS = [21, 22, 23]
const FEED_IN_FUEL_IDS = [31, 32, 33]

/* Frei benennbare Wärmequellen. Strom, Kälte und die Netzbezüge deckt der
   Katalog ab; Wärme ist die eine Nutzenergie, die aus beliebig vielen Quellen
   kommen kann — Flüssiggas, Biogas, Klärgas, zugekaufte Abwärme. Drei feste
   Plätze, weil `EnergyIDs.all` im Rechenkern als Positionsindex der
   Bilanzvektoren dient; siehe methods/basics.py. */
const CUSTOM_HEAT_FUEL_IDS = [24, 25, 26]
const HEAT_COMBUSTED = 'fuel'
const HEAT_DELIVERED = 'heat'

const HEAT_DELIVERY_OPTIONS = [
  { value: HEAT_COMBUSTED, title: 'Brennstoff' },
  { value: HEAT_DELIVERED, title: 'Gelieferte Wärme' },
]

function isCustomHeat(fuelId) {
  return CUSTOM_HEAT_FUEL_IDS.includes(fuelId)
}

function isLocallyProduced(fuelId) {
  return LOCALLY_PRODUCED_FUEL_IDS.includes(fuelId)
}

function isFeedIn(fuelId) {
  return FEED_IN_FUEL_IDS.includes(fuelId)
}

/* Ein Platz gilt als belegt, sobald er einen Namen, Werte oder Verbrauch trägt
   — nicht nur, wenn er in dieser Sitzung hinzugefügt wurde. Sonst verschwände
   eine gespeicherte Wärmequelle beim nächsten Laden aus dem Formular, während
   ihre Daten weiter in die Rechnung eingingen. */
const activeCustomHeatIds = computed(() =>
  CUSTOM_HEAT_FUEL_IDS.filter((id) =>
    addedCustomHeatIds.value.includes(id)
    || !!energyCustomName.value[id]
    || Number(energyPriceData.value[id]) > 0
    || Number(energyCo2Data.value[id]) > 0
    || Number(energyPerformanceData.value[id]) > 0
    || usedFuelIds.value.has(id))
)

const canAddCustomHeat = computed(
  () => activeCustomHeatIds.value.length < CUSTOM_HEAT_FUEL_IDS.length)

/* Die Bezugsseite: alles, was der Standort verbraucht — die Katalogträger, die
   drei lokal erzeugten Erneuerbaren und **am Ende** die selbst benannten
   Wärmequellen. Letztere stehen bewusst hinten und nur, wenn es sie gibt: als
   „Sonstige" noch ein fester Platz mitten in der Liste war, stand ein leeres
   Feld zwischen den Katalogträgern, das niemand ausfüllt.

   Der Anzeigename wird hier überschrieben, damit „Biogas" in jeder Tabelle
   steht — Verbrauch, Preis, Primärenergie, CO₂ — und nicht nur dort, wo er
   eingetippt wurde.

   Hiess vorher `fuelsConventional`; der Name war nie zutreffend. */
const fuelsSupply = computed(() => {
  const catalogue = fuels.value.filter((fuel) => {
    const isCatalogue = fuel.id <= 12
    const isRenewable = fuel.id >= 21 && fuel.id <= 23
    if (!isCatalogue && !isRenewable) return false
    return !fuel.deprecated || usedFuelIds.value.has(fuel.id)
  })

  const custom = activeCustomHeatIds.value
    .map((id) => fuels.value.find((fuel) => fuel.id === id))
    .filter(Boolean)

  return [...catalogue, ...custom].map((fuel) => ({
    ...fuel,
    name: energyCustomName.value[fuel.id] || fuel.name,
  }))
})

async function addCustomHeatSource() {
  const free = CUSTOM_HEAT_FUEL_IDS.find((id) => !activeCustomHeatIds.value.includes(id))
  if (free === undefined) return
  addedCustomHeatIds.value = [...addedCustomHeatIds.value, free]
  if (!energyHeatDelivery.value[free]) {
    energyHeatDelivery.value = { ...energyHeatDelivery.value, [free]: HEAT_COMBUSTED }
  }

  /* Die Tabelle scrollt innerhalb ihrer festen Höhe von 300 px. Die neue
     Zeile entsteht unten und damit ausserhalb des Sichtfelds — ohne dies
     passiert auf den Knopfdruck sichtbar nichts, und man drückt ein zweites
     Mal. Beim Ausprobieren im Browser sofort aufgefallen. */
  await nextTick()
  const wrapper = priceTable.value?.$el?.querySelector('.v-table__wrapper')
  if (wrapper) wrapper.scrollTop = wrapper.scrollHeight
}

/* Auch den Verbrauch der Jahre leeren: bliebe er stehen, hielte `usedFuelIds`
   den Platz belegt und die Zeile käme sofort zurück. */
function removeCustomHeatSource(fuelId) {
  addedCustomHeatIds.value = addedCustomHeatIds.value.filter((id) => id !== fuelId)
  energyCustomName.value = { ...energyCustomName.value, [fuelId]: '' }
  energyPriceData.value = { ...energyPriceData.value, [fuelId]: 0 }
  energyCo2Data.value = { ...energyCo2Data.value, [fuelId]: 0 }
  energyPerformanceData.value = { ...energyPerformanceData.value, [fuelId]: 0 }
  energyCo2MarketData.value = { ...energyCo2MarketData.value, [fuelId]: null }
  energyPerformanceTotalData.value = { ...energyPerformanceTotalData.value, [fuelId]: null }

  for (const perYear of Object.values(energyConsumptionData.value ?? {})) {
    if (perYear?.[fuelId]) perYear[fuelId] = { consumption: null, cost: null }
  }
  energyConsumptionData.value = { ...energyConsumptionData.value }
}

const localise = ref(false)
const geocoding = ref(false)
const lon = ref(null)
const lat = ref(null)
const locationSource = ref('address')
const locationResolvedAddress = ref('')
const addressSuggestion = ref(null)

const hasCoordinates = computed(
  () => Number.isFinite(Number(lat.value)) && Number.isFinite(Number(lon.value))
    && lat.value !== null && lon.value !== null
)

const year = ref(new Date().getFullYear())

const projectId = computed(() => {
  const raw = route.params.projectId
  if (raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})

const project = computed(() => store.currentProject)
const updating = computed(() => !!projectId.value)

const years = computed(() => {
  const todayYear = new Date().getFullYear()
  const result = []
  for (let y = todayYear; y >= todayYear - 10; y -= 1) result.push(y)
  return result
})

const yearIndex = computed(() => years.value.indexOf(year.value))

const owner = computed({
  get() {
    return ownerOverride.value ?? store.userId
  },
  set(val) {
    ownerOverride.value = val
  },
})

const {
  fieldErrors,
  valid,
  validate,
  getDataFromFields,
  setFieldsFromData,
  validationErrors,
  mergeTouched,
} = useValidation('/projects/validate/', fields)

const dataComputed = computed({
  get() {
    const stateObj = {
      owner: owner.value,
      name: name.value,
      note: note.value,
      postcode: postcode.value,
      street: street.value,
      city: city.value,
      lat: lat.value,
      lon: lon.value,
      locationSource: locationSource.value,
      locationResolvedAddress: locationResolvedAddress.value,
      branch: branch.value,
      category: category.value,
      area: area.value,
      creditExportedEnergy: creditExportedEnergy.value,
      energyDataPayload: energyDataPayload.value,
      energyConsumptionDataPayload: energyConsumptionDataPayload.value,
    }
    return getDataFromFields(stateObj)
  },
  set(payload) {
    const targetState = {
      owner: owner.value,
      name: name.value,
      note: note.value,
      postcode: postcode.value,
      street: street.value,
      city: city.value,
      lat: lat.value,
      lon: lon.value,
      locationSource: locationSource.value,
      locationResolvedAddress: locationResolvedAddress.value,
      branch: branch.value,
      category: category.value,
      area: area.value,
      creditExportedEnergy: creditExportedEnergy.value,
      energyDataPayload: energyDataPayload.value,
      energyConsumptionDataPayload: energyConsumptionDataPayload.value,
    }
    setFieldsFromData(targetState, payload)

    owner.value = targetState.owner
    name.value = targetState.name
    note.value = targetState.note
    postcode.value = targetState.postcode
    street.value = targetState.street
    city.value = targetState.city
    lat.value = targetState.lat
    lon.value = targetState.lon
    locationSource.value = targetState.locationSource
    locationResolvedAddress.value = targetState.locationResolvedAddress
    branch.value = targetState.branch
    category.value = targetState.category
    area.value = targetState.area
    creditExportedEnergy.value = targetState.creditExportedEnergy

    energyDataPayload.value = targetState.energyDataPayload
    energyConsumptionDataPayload.value = targetState.energyConsumptionDataPayload
  },
})

/*
 * `performance_total` und `co2_market_based` sind ausdrücklich optional: leer
 * heisst „kein abweichender Nachweis", und der Server fällt dann auf den
 * Katalogwert bzw. den standortbasierten Faktor zurück. Eine leere Eingabe darf
 * deshalb nicht als 0 durchgehen — 0 wäre die Behauptung, der Strom sei
 * emissionsfrei. Die beiden Pflichtfelder daneben behalten ihr `?? 0`.
 */
function optionalNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const energyDataPayload = computed({
  get() {
    return fuels.value.map((fuel) => ({
      fuel: fuel.id,
      price: energyPriceData.value[fuel.id] ?? 0,
      co2: energyCo2Data.value[fuel.id] ?? 0,
      co2_market_based: optionalNumber(energyCo2MarketData.value[fuel.id]),
      performance: energyPerformanceData.value[fuel.id] ?? 0,
      performance_total: optionalNumber(energyPerformanceTotalData.value[fuel.id]),
      // Der Server weist eine Bezeichnung an einem Katalogträger zurück — der
      // führt seinen Namen selbst.
      custom_name: isCustomHeat(fuel.id) ? (energyCustomName.value[fuel.id] || '') : '',
      heat_delivery: energyHeatDelivery.value[fuel.id] || HEAT_COMBUSTED,
    }))
  },
  set(data) {
    for (const fuelData of data) {
      energyPriceData.value[fuelData.fuel] = fuelData.price
      energyPerformanceData.value[fuelData.fuel] = fuelData.performance
      energyPerformanceTotalData.value[fuelData.fuel] = fuelData.performance_total
      energyCo2Data.value[fuelData.fuel] = fuelData.co2
      energyCo2MarketData.value[fuelData.fuel] = fuelData.co2_market_based
      energyCustomName.value[fuelData.fuel] = fuelData.custom_name || ''
      energyHeatDelivery.value[fuelData.fuel] = fuelData.heat_delivery || HEAT_COMBUSTED
    }
    energyPriceData.value = { ...energyPriceData.value }
    energyPerformanceData.value = { ...energyPerformanceData.value }
    energyPerformanceTotalData.value = { ...energyPerformanceTotalData.value }
    energyCo2Data.value = { ...energyCo2Data.value }
    energyCo2MarketData.value = { ...energyCo2MarketData.value }
    energyCustomName.value = { ...energyCustomName.value }
    energyHeatDelivery.value = { ...energyHeatDelivery.value }
  },
})

const energyConsumptionDataPayload = computed({
  get() {
    return Object.values(energyConsumptionData.value)
      .map((yearData, i) =>
        fuels.value.map((fuel) => ({
          year: years.value[i],
          fuel: fuel.id,
          consumption: yearData[fuel.id]?.consumption ?? null,
          cost: yearData[fuel.id]?.cost ?? null,
        })),
      )
      .flat()
      .filter((x) => x.consumption || x.cost)
  },
  set(data) {
    for (const entry of data) {
      const idx = years.value.indexOf(entry.year)
      if (idx < 0) continue
      if (!energyConsumptionData.value[idx]) energyConsumptionData.value[idx] = {}
      energyConsumptionData.value[idx][entry.fuel] = {
        consumption: entry.consumption,
        cost: entry.cost,
      }
    }
    energyConsumptionData.value = { ...energyConsumptionData.value }
  },
})

/*
 * Die Fehlerliste des Servers steht in der Reihenfolge von `energyDataPayload`,
 * also über *alle* Energieträger. Die Tabellen zeigen jeweils nur einen
 * Ausschnitt — `fuelsSupply` bzw. `fuelsFD`. Solange über den
 * Schleifenindex zugegriffen wurde, landete der Fehler in einer fremden Zeile:
 * bei der Einspeisevergütung stand die Meldung für Netzstrom, weil deren erste
 * Zeile der Index 0 ist. Deshalb auf die Energieträger-ID umschlüsseln, wie es
 * `getEnergyConsumptionErrorsForFuels` für die Verbrauchstabellen längst tut.
 */
function energyDataErrorsByFuel(field) {
  const payloadErrors = fieldErrors.value.energyDataPayload
  if (!payloadErrors) return {}

  return fuels.value.reduce((result, fuel, index) => {
    result[fuel.id] = payloadErrors[index]?.[field]
    return result
  }, {})
}

const energyPriceDataErrors = computed(() => energyDataErrorsByFuel('price'))
const energyPerformanceDataErrors = computed(() => energyDataErrorsByFuel('performance'))
const energyPerformanceTotalDataErrors = computed(
  () => energyDataErrorsByFuel('performance_total'))
const energyCo2DataErrors = computed(() => energyDataErrorsByFuel('co2'))
const energyCo2MarketDataErrors = computed(() => energyDataErrorsByFuel('co2_market_based'))
const energyCustomNameErrors = computed(() => energyDataErrorsByFuel('custom_name'))


const supplyConsumptionErrors = computed(() => {
  return getEnergyConsumptionErrorsForFuels(
    fuelsSupply.value
  )
})

const feedConsumptionErrors = computed(() => {
  return getEnergyConsumptionErrorsForFuels(
    fuelsFD.value
  )
})

onBeforeMount(() => {
  fetchBranches()
  fetchFuels()
  fetchHints()
  fetchProject()
})

watch(branch, () => {
  const branchObject = branches.value.find((b) => b.id === branch.value)
  categories.value = branchObject?.categories ?? []
  if (categories.value.length === 1) {
    category.value = categories.value[0].id
  }
})

watch(validationErrors, () => {
  mergeTouched()
})

/*
 * Warnung vor ungespeicherten Eingaben.
 *
 * Verglichen wird genau die Nutzlast, die beim Speichern an den Server geht —
 * nicht die einzelnen Felder. So schlägt die Warnung nicht an, wenn sich nur
 * die Darstellung ändert (etwa die Karte aufklappt).
 */
const { merkeStand } = useUnsavedChanges(() => dataComputed.value)

watch(dataComputed, (newData, oldData) => {
  if (JSON.stringify(newData) !== JSON.stringify(oldData)) {
    validate(newData)
  }
})

watch(projectId, fetchProject, { immediate: true })
watch(fuels, fetchProject)

watch(project, (p) => {
  if (p) {
    store.setTitle(p.name ?? null)
  }
})

watch(fieldErrors, () => {
  energyConsumptionDataLastPayload.value = energyConsumptionDataPayload.value
})

function getEnergyConsumptionErrorsForFuels(fuelList) {
  const payloadErrors =
    fieldErrors.value.energyConsumptionDataPayload

  if (!payloadErrors) {
    return []
  }

  const fuelErrorMap =
    energyConsumptionDataLastPayload.value
      .map((entry, index) => ({
        ...entry,
        errorIndex: index,
      }))
      .filter(entry => entry.year === year.value)
      .reduce((result, entry) => {
        result[entry.fuel] =
          payloadErrors[entry.errorIndex] || {}

        return result
      }, {})

  return fuelList.map(fuel => {
    return fuelErrorMap[fuel.id] || {}
  })
}

/*
 * Über die ID, nicht über den Anzeigenamen: der Name ist beschriftungsabhängig
 * (aus „Erdgas" wurde „Erdgas H") und muss nicht eindeutig sein — ein
 * umbenannter Träger hätte den Knopf stillschweigend wirkungslos gemacht.
 *
 * Für den marktbasierten CO₂-Faktor gibt es bewusst keinen Standardwert: er
 * kommt aus dem Liefervertrag, nicht aus einem Katalog.
 */
function getStandardParameter(fuelId, field) {
  const fuel = fuels.value.find((f) => f.id === fuelId)
  if (!fuel) return

  /* Für die Erneuerbaren und „Sonstige" führt der Katalog keine Werte. Ohne
     diese Schranke hat der Knopf ein ausgefülltes Feld geleert, statt nichts
     zu tun — ein Standardwert, den es nicht gibt, ist kein Standardwert. */
  const catalogue = {
    energyPriceData: fuel.price,
    energyPerformanceData: fuel.primary_energy_factor,
    energyPerformanceTotalData: fuel.primary_energy_factor_total,
    energyCo2Data: fuel.co2_emission_factor,
  }[field]
  if (catalogue === null || catalogue === undefined) return

  if (field === 'energyPriceData') {
    energyPriceData.value[fuel.id] = fuel.price
  } else if (field === 'energyPerformanceData') {
    energyPerformanceData.value[fuel.id] = fuel.primary_energy_factor
  } else if (field === 'energyPerformanceTotalData') {
    energyPerformanceTotalData.value[fuel.id] = fuel.primary_energy_factor_total
  } else if (field === 'energyCo2Data') {
    energyCo2Data.value[fuel.id] = fuel.co2_emission_factor
  }
}

/*
 * Adresse zu Koordinaten auflösen. Ausdrücklich getrennt vom Ein- und
 * Ausblenden der Karte: solange beides an einem Knopf hing, hat das Schließen
 * der Karte eine zweite, überflüssige Anfrage ausgelöst — und nach einem
 * Fehlschlag ließ sich die Karte gar nicht mehr schließen.
 */
async function runLocalise() {
  if (geocoding.value) return

  geocoding.value = true
  try {
    /*
     * Über den eigenen Server statt direkt zu Nominatim: nur dort lassen sich
     * der von der Nutzungsrichtlinie geforderte User-Agent (den ein Browser
     * gar nicht setzen kann), die Ratenbegrenzung und der Cache umsetzen.
     * Siehe projects/geocoding.py.
     */
    const resp = await api.get('/projects/geocode/', {
      params: {
        street: street.value,
        city: city.value,
        postcode: postcode.value,
        // Vorerst nur der beste Treffer. Die Auswahl unter mehreren Kandidaten
        // kommt mit der Trefferqualitäts-Anzeige.
        limit: 1,
      },
    })

    // Der Server liefert bereits Zahlen und eine normalisierte Form.
    const hit = resp.data?.results?.[0]

    if (!hit) {
      toast.error('Zu dieser Adresse wurde kein Standort gefunden. Bitte prüfen Sie die Adresse.')
      return
    }

    lat.value = hit.latitude
    lon.value = hit.longitude
    locationResolvedAddress.value = hit.display_name ?? ''
    locationSource.value = 'address'
    localise.value = true
  } catch (error) {
    /*
     * Vom „nicht gefunden" oben unterschieden: hier ist der Dienst nicht
     * erreichbar, die Adresse kann völlig richtig sein. Der Server schickt
     * eine genauere Ursache mit — etwa den Hinweis auf das CA-Bundle, wenn
     * ein Proxy die TLS-Verbindung aufbricht.
     */
    toast.error(
      error?.response?.data?.detail
      || 'Der Kartendienst ist nicht erreichbar. Bitte später erneut versuchen.'
    )
  } finally {
    geocoding.value = false
  }
}

function toggleMap() {
  localise.value = !localise.value
}

/*
 * Marker verschoben. Die Koordinaten werden sofort übernommen — das ist die
 * ausdrückliche Handlung des Nutzers — und als „manuell" markiert, damit eine
 * spätere Adressänderung die Korrektur nicht stillschweigend überschreibt.
 *
 * Die zugehörige Adresse wird nur *vorgeschlagen*. Bei einem großen Werksgelände
 * ist die nächstgelegene OSM-Adresse häufig die Nachbarstraße; die eingetippte
 * Adresse ist dann die bessere.
 */
async function onMarkerMoved({ latitude, longitude }) {
  lat.value = latitude
  lon.value = longitude
  locationSource.value = 'manual'
  addressSuggestion.value = null

  try {
    const { data } = await api.get('/projects/reverse-geocode/', {
      params: { latitude, longitude },
    })

    if (data?.result) {
      addressSuggestion.value = data.result
      locationResolvedAddress.value = data.result.display_name ?? ''
    } else {
      // Freie Fläche ohne Adresse — kein Fehler, nur nichts vorzuschlagen.
      locationResolvedAddress.value = ''
    }
  } catch {
    // Der Standort steht trotzdem; nur der Adressvorschlag fehlt.
    addressSuggestion.value = null
  }
}

function applyAddressSuggestion() {
  const suggestion = addressSuggestion.value
  if (!suggestion) return

  if (suggestion.street) street.value = suggestion.street
  if (suggestion.postcode) postcode.value = suggestion.postcode
  if (suggestion.city) city.value = suggestion.city

  addressSuggestion.value = null
}

/* Zurück auf den aus der Adresse ermittelten Standort. */
function resetLocationToAddress() {
  addressSuggestion.value = null
  locationSource.value = 'address'
  runLocalise()
}

async function fetchHints() {
  try {
    const response = await api.get('/projects/help/')
    hint.value = Object.assign({}, ...response.data.map((x) => ({ [x.key]: x.help })))
  } catch {
    hint.value = {}
  }
}

async function fetchProject() {
  if (!projectId.value || !fuels.value.length) return
  await store.fetchCurrentProject(projectId.value)
  const p = store.currentProject
  if (p) {
    const tmpTarget = {
      owner: owner.value,
      name: name.value,
      note: note.value,
      postcode: postcode.value, // FIXED: removed stray text
      street: street.value,
      city: city.value,
      lat: lat.value,
      lon: lon.value,
      locationSource: locationSource.value,
      locationResolvedAddress: locationResolvedAddress.value,
      branch: branch.value,
      category: category.value,
      area: area.value,
      creditExportedEnergy: creditExportedEnergy.value,
      energyDataPayload: energyDataPayload.value,
      energyConsumptionDataPayload: energyConsumptionDataPayload.value,
    }
    setFieldsFromData(tmpTarget, p)
    owner.value = tmpTarget.owner
    name.value = tmpTarget.name
    note.value = tmpTarget.note
    postcode.value = tmpTarget.postcode
    street.value = tmpTarget.street
    city.value = tmpTarget.city
    lat.value = tmpTarget.lat
    lon.value = tmpTarget.lon
    locationSource.value = tmpTarget.locationSource ?? 'address'
    locationResolvedAddress.value = tmpTarget.locationResolvedAddress ?? ''
    branch.value = tmpTarget.branch
    category.value = tmpTarget.category
    area.value = tmpTarget.area
    creditExportedEnergy.value = tmpTarget.creditExportedEnergy
    energyDataPayload.value = tmpTarget.energyDataPayload
    energyConsumptionDataPayload.value = tmpTarget.energyConsumptionDataPayload

    // Gespeicherte Koordinaten direkt zeigen, ohne erneute Anfrage.
    localise.value = hasCoordinates.value

    // Der geladene Stand ist der gespeicherte — erst Eingaben danach gelten
    // als ungespeichert.
    merkeStand()
  }
}

async function submit() {
  const payload = dataComputed.value
  try {
    saving.value = true
    if (updating.value && projectId.value) {
      const response = await api.put(`/projects/${projectId.value}/`, payload)
      toast.success(`Projekt ${response.data.name} erfolgreich gespeichert.`)
      merkeStand()
      router.push({ name: 'ProjectDashboard', params: { projectId: response.data.id } })
    } else {
      const response = await api.post('/projects/', payload)
      toast.success(`Projekt ${response.data.name} erfolgreich erstellt.`)
      merkeStand()
      router.push({ name: 'ProjectDashboard', params: { projectId: response.data.id } })
    }
  } catch {
    toast.error('Es gab einen Fehler beim Erstellen des Projektes.')
  } finally {
    saving.value = false
  }
}

async function fetchBranches() {
  try {
    const response = await api.get('/projects/branches/')
    branches.value = response.data
    if (branches.value.length === 1) {
      branch.value = branches.value[0].id
    }
  } catch {
    // ignore
  }
}

async function fetchFuels() {
  try {
    const response = await api.get('/projects/fuels_ee/')
    const list = response.data

    energyConsumptionData.value = years.value.map(() => {
      return list.reduce((acc, curr) => {
        acc[curr.id] = { consumption: null, cost: null }
        return acc
      }, {})
    })

    fuels.value = list
    fuelsFD.value = list.filter((f) => f.id >= 31 && f.id <= 33)

    /* Nur die Pflichtangaben aus dem Katalog vorbelegen. Die beiden optionalen
       Felder bleiben leer — leer heisst „Katalogwert bzw. standortbasierter
       Faktor gilt", und genau das ist der Vorgabefall. */
    for (const f of list) {
      if (energyPriceData.value[f.id] == null) energyPriceData.value[f.id] = f.price
      if (energyPerformanceData.value[f.id] == null) energyPerformanceData.value[f.id] = f.primary_energy_factor
      if (energyCo2Data.value[f.id] == null) energyCo2Data.value[f.id] = f.co2_emission_factor
    }
  } catch (error) {
    // Die Fehlervariable war vorher nicht gebunden — der `console.error` lief
    // selbst in einen ReferenceError und verbarg damit die eigentliche Ursache.
    console.error('Fehler beim Laden der Energieträger:', error)
  }
}
</script>

<style scoped lang="scss">
.project-details-page {
  padding-bottom: 1rem;
}

/* Main cards */
.details-card {
  overflow: hidden;
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.details-card-header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1.25rem 1.5rem;
  background:
    linear-gradient(
      180deg,
      rgba(var(--v-theme-primary), 0.07),
      rgba(var(--v-theme-primary), 0.025)
    );
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.details-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.details-card-body {
  padding: 1.25rem 1.5rem 1.5rem;
}

/* Inputs */
:deep(.v-field) {
  border-radius: 18px;
}

:deep(.v-input) {
  margin-bottom: 0.35rem;
}

.details-help-button {
  text-transform: none;
  font-weight: 600;
}

/* Headings inside expansion panels */
div.heading {
  color: rgb(var(--v-theme-on-surface));
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.35;
}

/* Expansion panels */
:deep(.v-expansion-panels) {
  gap: 0.85rem;
}

:deep(.v-expansion-panel) {
  border-radius: 22px !important;
  overflow: hidden;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

:deep(.v-expansion-panel-title) {
  min-height: 58px;
  padding-inline: 1.25rem;
  background-color: rgba(var(--v-theme-primary), 0.035);
}

:deep(.v-expansion-panel-title:hover) {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 1.25rem;
}

/* Scroll areas */
.energy-year-scroll {
  max-height: 300px;
  overflow-y: auto;
  border-radius: 18px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background-color: rgb(var(--v-theme-surface));
}

/* Tables */
.energy-table {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background-color: rgb(var(--v-theme-surface));
}

.energy-table :deep(thead th) {
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
  background-color: rgb(var(--v-theme-surface));
  box-shadow: 0 1px 0 rgba(var(--v-theme-on-surface), 0.08);
}

.energy-table :deep(tbody tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.055);
}

/* Namensfeld, Lieferart und Entfernen-Knopf einer eigenen Wärmequelle. */
.custom-heat-cell {
  min-width: 230px;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

/* Hinweis unter dem Trägernamen, wo ein Preis von 0 zulässig ist. */
.fuel-note {
  color: rgba(var(--v-theme-on-surface), 0.58);

  font-size: 0.72rem;
  line-height: 1.2;
}

/* Stats card wrapper */
:deep(.v-card) {
  border-radius: 28px;
}

/* Standard value icon */
.standard-value-btn {
  opacity: 0.75;
  color: rgb(var(--v-theme-primary)) !important;
}

.standard-value-btn:hover {
  opacity: 1;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Map */
:deep(.leaflet-container),
:deep(.map-container) {
  border-radius: 28px;
  overflow: hidden;
}

/* Save area */
.details-save-card {
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.details-save-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
}

.save-button {
  min-width: 150px;
  text-transform: none;
  font-weight: 600;
}

/* Localise button */
.v-btn {
  text-transform: none;
}

/* Responsive */
@media (max-width: 960px) {
  .details-card-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .details-help-button {
    width: 100%;
  }

  .details-save-content {
    align-items: stretch;
    flex-direction: column;
  }

  .save-button {
    width: 100%;
  }
}

@media (max-width: 700px) {
  .details-card-body {
    padding: 1rem;
  }

  .details-card-header {
    padding: 1rem;
  }

  .details-header-icon {
    width: 42px;
    height: 42px;
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 1rem;
  }
}
</style>
